import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Logger} from 'app/error-handling/services/logger/logger.service';
import {JiraIssue} from 'app/interfaces';
import {environment} from 'environments/environment';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class JiraService {
  private static readonly documentsUrl: string = environment.apiHost + '/jira/documents';
  private static readonly documentUrlPrefix: string = environment.apiHost + '/jira/document/';

  constructor(private _http: HttpClient, private _snackbar: MatSnackBar) {}

  public getJiraDocuments(): Observable<JiraIssue[]> {
    return this._http.get(JiraService.documentsUrl).pipe(
      map((issues: any) => this.toJiraIssues(issues)),
      catchError((e) => {
        Logger.error('jira-error', 'Failed to GET Jira documents from API.', e);
        return of([]);
      })
    );
  }

  public getJiraDocument(documentId: string): Observable<JiraIssue> {
    return this._http.get(JiraService.documentUrlPrefix + documentId).pipe(
      map((issue: any) => this.toJiraIssue(issue)),
      catchError((e) => {
        Logger.error('jira-error', 'Failed to GET Jira document from API.', e);
        return of(null);
      })
    );
  }

  private toJiraIssues(objects: any[]): JiraIssue[] {
    return objects.map(this.toJiraIssue);
  }

  private toJiraIssue(object: any): JiraIssue {
    if (object['extras']) {
      const extras: Map<string, string> = new Map(Object.entries(object['extras']));
      extras.forEach((val, key) => {
        const timestamp: number = Date.parse(val);
        if (!isNaN(timestamp)) {
          extras.set(key, new Date(timestamp).toDateString());
        }
      });
      object['extras'] = extras;
    }
    return object as JiraIssue;
  }

  /**
   * Refreshes the cached states of Jira documents within the CARS service.
   */
  public refreshJiraStates(): Observable<boolean> {
    return this._http.get(environment.apiHost + '/jira/clear').pipe(
      map(() => {
        this._snackbar.open('Successfully refreshed Jira linkage.', 'Dismiss', {duration: 5000});
        return true;
      }),
      catchError((e) => {
        this._snackbar.open('Failed to refresh Jira linkage.', 'Dismiss', {duration: 5000});
        Logger.error('jira-error', 'Failed to refresh Jira linkage.', e);
        return of(false);
      })
    );
  }
}
